/**
 * Data Events Service
 *
 * Simple event system to notify UI when data changes.
 * Pages/hooks subscribe to these events and auto-refresh.
 *
 * Usage:
 * - Services emit: dataEvents.emit('transactions')
 * - Hooks listen: dataEvents.on('transactions', refresh)
 */

// Event types for different data entities
export type DataEventType = 'transactions' | 'accounts' | 'categories' | 'all';

type Listener = () => void;

class DataEventEmitter {
  private listeners: Map<DataEventType, Set<Listener>> = new Map();

  /**
   * Subscribe to data change events.
   * Returns unsubscribe function.
   */
  on(event: DataEventType, listener: Listener): () => void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add(listener);

    // Return unsubscribe function
    return () => {
      this.listeners.get(event)?.delete(listener);
    };
  }

  /**
   * Emit a data change event.
   * All listeners for this event type (and 'all') will be notified.
   */
  emit(event: DataEventType): void {
    // Notify specific event listeners
    this.listeners.get(event)?.forEach((listener) => {
      try {
        listener();
      } catch (err) {
        console.error('[DataEvents] Listener error:', err);
      }
    });

    // Also notify 'all' listeners (for pages that care about any data change)
    if (event !== 'all') {
      this.listeners.get('all')?.forEach((listener) => {
        try {
          listener();
        } catch (err) {
          console.error('[DataEvents] Listener error:', err);
        }
      });
    }
  }

  /**
   * Get count of listeners for debugging.
   */
  getListenerCount(event: DataEventType): number {
    return this.listeners.get(event)?.size || 0;
  }
}

// Singleton instance
export const dataEvents = new DataEventEmitter();
